import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as React from 'react'
import noop from 'lib/function/noop'
import GeoContext from 'contexts/geoContext'
import { useGetChat } from 'tripPlanner/hooks/api/chat'
import ChatLandingPage from '../ChatLandingPage/ChatLandingPage'
import TripPlannerHeader from '../TripPlannerHeader'
import PaneResizer from 'tripPlanner/components/PaneResizer'
import { getElementTextDirection } from 'lib/dom/logicalScroll'
import { clamp } from 'lib/maths/mathUtils'
import {
  MainLayout,
  ContentWrapper,
  MainContent,
  ChatColumnLayout,
  LandingPageWrapper,
  CHAT_DEFAULT_WIDTH, CHAT_MAX_WIDTH, CHAT_MIN_WIDTH,
  LEFT_PANE_WIDTH_VAR,
  RIGHT_PANEL_MIN_WIDTH,
} from './styles/layoutStyles'
import { TripListPricingProvider } from './contexts/TripListPricingContext'
import { useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useChatActions } from './contexts/ChatActionsContext'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useChatStreamMessages } from './contexts/ChatStreamMessagesContext'
import { MapInteractionProvider } from './contexts/MapInteractionContext'
import {
  DEFAULT_MAP_STYLE_MODE,
  type MapStyleMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'
import type { ChatNavigationOptions } from './hooks/useChatNavigation'
import { useMessageContext } from './hooks/useMessageContext'
import ChatContainer from './containers/ChatContainer'
import StaffSpoofBanner from './components/StaffSpoofBanner'
import PrivateChatBanner from 'tripPlanner/components/PrivateChatBanner'
import CustomerVisibleChatBanner from 'tripPlanner/components/CustomerVisibleChatBanner'
import { selectSpoofBanner } from 'tripPlanner/utils/selectSpoofBanner'
import { useSpoofSendGuard } from 'tripPlanner/hooks/useSpoofSendGuard'
import RightPanelContainer from './containers/RightPanelContainer'
import DrawerContainer from './containers/DrawerContainer'
import { addBookingItemIdsToSelection } from './utils/bookingSelection'
import { useChatSessionTracking } from './hooks/useChatSessionTracking'
import { usePromptTokenSubmission } from './hooks/usePromptTokenSubmission'
import { useChatRouteSync } from './hooks/useChatRouteSync'
import { TripDrawerPresentationProvider } from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'

// ── Props interface ──

const STAFF_VIEW_QUERY_PARAM = 'staffView'

interface Props {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  isLoggedIn: boolean
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  canViewStaffChat: boolean
  staffViewedChatId: number | undefined
  onStaffViewedChatIdChange: (chatId: number | undefined) => void
  onDevModeToggle: () => void
  setBookingSelection: React.Dispatch<React.SetStateAction<{
    orderIds: Array<string>
    itemIds: Array<string>
  }>>
  selectedBookingItemIds?: Array<string>
  prefilledOrderChipLabels: Array<string>
  onRemovePrefilledOrderTag: (tag: string) => void
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

// ── Orchestrator: reads from contexts, wires containers ──

function ChatPageContent({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  isLoggedIn,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  canViewStaffChat,
  staffViewedChatId,
  onStaffViewedChatIdChange,
  onDevModeToggle,
  setBookingSelection,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  prefilledOrderChipLabels,
  onRemovePrefilledOrderTag,
}: Props) {
  const eventBus = useTripPlannerEventBus()
  const { search } = useLocation()

  // ── Read from contexts ──

  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const { messages } = useChatStreamMessages()

  // ── Chat input ref (replaces focus_chat_input event) ──

  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)

  // ── Hover state (MapInteractionContext) ──

  const [hoveredHotelId, setHoveredHotelId] = useState<string | undefined>(undefined)
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>(DEFAULT_MAP_STYLE_MODE)
  const [isStaffSpoofBannerDismissed, setIsStaffSpoofBannerDismissed] = useState(false)
  const [isPrivateChatBannerDismissed, setIsPrivateChatBannerDismissed] = useState(false)
  const [isCustomerVisibleChatBannerDismissed, setIsCustomerVisibleChatBannerDismissed] = useState(false)
  const isCardHoveredRef = useRef(false)

  const handleMarkerHover = useCallback((markerId: string | undefined) => {
    if (markerId === undefined && isCardHoveredRef.current) return
    setHoveredHotelId(markerId)
  }, [])

  const handleCardHover = useCallback((offerId: string | undefined) => {
    isCardHoveredRef.current = offerId !== undefined
    setHoveredHotelId(offerId)
  }, [])

  const mapInteractionValue = useMemo(() => ({
    onCardHover: handleCardHover,
    onMarkerHover: handleMarkerHover,
    highlightedMarkerId: hoveredHotelId,
    mapStyleMode,
    onMapStyleModeChange: setMapStyleMode,
  }), [handleCardHover, handleMarkerHover, hoveredHotelId, mapStyleMode])

  // ── Pane resizer ──

  const mainContentRef = useRef<HTMLDivElement>(null)
  const leftPaneWidth = useRef<number>(CHAT_DEFAULT_WIDTH)

  const handlePaneDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()

    const handleDragging = (e: MouseEvent) => {
      const mainContentElement = mainContentRef.current
      if (!mainContentElement) return
      const rect = mainContentElement.getBoundingClientRect()
      const containerWidth = rect.width
      const minLeft = CHAT_MIN_WIDTH
      const maxLeft = Math.max(
        CHAT_MIN_WIDTH,
        Math.min(CHAT_MAX_WIDTH, containerWidth - RIGHT_PANEL_MIN_WIDTH),
      )
      const inlineStartOffset = getElementTextDirection(mainContentElement) === 'rtl' ?
        rect.right - e.clientX :
        e.clientX - rect.left
      const newWidth = clamp(minLeft, inlineStartOffset, maxLeft)
      leftPaneWidth.current = newWidth
      mainContentElement.style.setProperty(LEFT_PANE_WIDTH_VAR, `${newWidth}px`)
    }

    const handleDragEnd = () => {
      window.removeEventListener('mousemove', handleDragging)
      window.removeEventListener('mouseup', handleDragEnd)
    }

    window.addEventListener('mousemove', handleDragging)
    window.addEventListener('mouseup', handleDragEnd)
  }, [])

  // ── chatIdRef for async operations ──

  const chatIdRef = useRef(chatId)
  useEffect(() => {
    chatIdRef.current = chatId
  }, [chatId])

  // ── hiddenFromCustomer read (deduped React Query cache read, no new fetch) ──

  const { currentRegionCode } = useContext(GeoContext)
  const { data: chatData } = useGetChat(chatId, currentRegionCode)
  const hiddenFromCustomer = chatData?.hiddenFromCustomer ?? true

  // ── Analytics: session start + first message tracking ──

  const trackFirstMessage = useChatSessionTracking({ chatId, existingRouteChatId })

  // ── useMessageContext (stays in orchestrator — needed by both landing page and ChatContainer) ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    // ChatContainer owns messageInputValue state and ChatMessageInput clears itself
    // on submit. The orchestrator doesn't need a real setter here.
    setMessageInputValue: noop,
    focusChatInput,
    prefilledMultiChoiceSelection: prefilledOrderChipLabels,
  })

  const { confirmOrAbort } = useSpoofSendGuard({ isSpoofed, hiddenFromCustomer, chatId })

  const handleSubmitWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return handleSubmit(message)
  }, [confirmOrAbort, handleSubmit, trackFirstMessage])

  const submitDirectWithTracking = useCallback(async(message: string) => {
    if (!(await confirmOrAbort())) return
    trackFirstMessage()
    return submitDirect(message)
  }, [confirmOrAbort, submitDirect, trackFirstMessage])

  const isStaffViewRoute = useMemo(() => {
    return new URLSearchParams(search).get(STAFF_VIEW_QUERY_PARAM) === '1'
  }, [search])

  usePromptTokenSubmission({
    search,
    isNewChatRoute,
    isLoading,
    isStreaming,
    onPromptSubmit: handleSubmitWithTracking,
  })

  const handleAddTags = useCallback((tags: Array<string>) => {
    const deduplicated = tags.filter(tag => !activeMultiChoiceSelection.includes(tag))
    if (deduplicated.length > 0) {
      handleMultiChoiceSelectionChange([...activeMultiChoiceSelection, ...deduplicated])
    }
  }, [handleMultiChoiceSelectionChange, activeMultiChoiceSelection])

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setBookingSelection(previous => addBookingItemIdsToSelection(previous, bookingItemIds))
  }, [setBookingSelection])

  const landingPageTags = useMemo(() => {
    return [...new Set([...prefilledOrderChipLabels, ...activeMultiChoiceSelection])]
  }, [activeMultiChoiceSelection, prefilledOrderChipLabels])

  const handleLandingPageTagRemove = useCallback((tag: string) => {
    if (prefilledOrderChipLabels.includes(tag)) {
      onRemovePrefilledOrderTag(tag)
      return
    }

    handleRemoveTag(tag)
  }, [handleRemoveTag, onRemovePrefilledOrderTag, prefilledOrderChipLabels])

  // ── Chat navigation ──

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    // Reset orchestrator-local state
    resetSelectionState()
    setHoveredHotelId(undefined)
    setMapStyleMode(DEFAULT_MAP_STYLE_MODE)
    isCardHoveredRef.current = false
  }, [eventBus, resetSelectionState])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [syncChatId, resetConversation, resetChatSpecificState])

  const handleStartNewChat = useCallback(() => {
    onStaffViewedChatIdChange(undefined)
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, resetConversationState, onStaffViewedChatIdChange])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === undefined || selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    chatIdRef.current = selectedChatId
    syncChatId(selectedChatId, options)
    setFollowTail(false)
  }, [
    chatId,
    setFollowTail,
    syncChatId,
    resetChatSpecificState,
    resetStreamingState,
  ])

  const handleSelectChatFromHistory = useCallback((selectedChatId: number) => {
    onStaffViewedChatIdChange(undefined)
    loadChatById(selectedChatId)
  }, [loadChatById, onStaffViewedChatIdChange])

  const handleStaffChatOpen = useCallback((selectedChatId: number) => {
    if (!canViewStaffChat || selectedChatId === chatId) {
      return
    }

    onStaffViewedChatIdChange(selectedChatId)
    loadChatById(selectedChatId, { search: `${STAFF_VIEW_QUERY_PARAM}=1` })
  }, [canViewStaffChat, chatId, loadChatById, onStaffViewedChatIdChange])

  // ── Route change effect ──

  useEffect(() => {
    setIsStaffSpoofBannerDismissed(false)
  }, [staffViewedChatId])

  // A dismissal on one chat shouldn't hide the banner on a different chat.
  useEffect(() => {
    setIsPrivateChatBannerDismissed(false)
    setIsCustomerVisibleChatBannerDismissed(false)
  }, [chatId])

  useEffect(() => {
    if (canViewStaffChat) {
      return
    }

    onStaffViewedChatIdChange(undefined)
  }, [canViewStaffChat, onStaffViewedChatIdChange])

  useEffect(() => {
    if (existingRouteChatId !== undefined) {
      if (canViewStaffChat && isStaffViewRoute && staffViewedChatId !== existingRouteChatId) {
        onStaffViewedChatIdChange(existingRouteChatId)
      } else if (staffViewedChatId !== undefined && existingRouteChatId !== staffViewedChatId) {
        onStaffViewedChatIdChange(undefined)
      }
    }
  }, [canViewStaffChat, isStaffViewRoute, existingRouteChatId, staffViewedChatId, onStaffViewedChatIdChange])

  useChatRouteSync({
    chatId,
    existingRouteChatId,
    isLandingRoute,
    isNewChatRoute,
    loadChatById,
    resetConversationState,
  })

  // ── Chat deleted handler (navigation only — header owns the delete flow) ──

  const handleChatDeleted = useCallback((deletedChatId: number, fallbackChatId: number | undefined) => {
    if (chatId === deletedChatId) {
      onStaffViewedChatIdChange(undefined)
      if (fallbackChatId) {
        loadChatById(fallbackChatId)
      } else {
        resetConversationState()
      }
    }
  }, [chatId, loadChatById, resetConversationState, onStaffViewedChatIdChange])

  // ── Header ──

  const tripPlannerHeader = useMemo(() => (
    <TripPlannerHeader
      chatId={chatId}
      onStartNewChat={handleStartNewChat}
      onChatSelect={handleSelectChatFromHistory}
      onChatDeleted={handleChatDeleted}
      isDevMode={isDevMode}
      onDevModeToggle={onDevModeToggle}
    />
  ), [chatId, handleStartNewChat, handleSelectChatFromHistory, handleChatDeleted, isDevMode, onDevModeToggle])

  // ── Render ──

  const hasMessages = messages.length > 0
  const showStaffSpoofBanner = !isStaffSpoofBannerDismissed && canViewStaffChat && chatId !== undefined && chatId === staffViewedChatId
  const spoofBanner = selectSpoofBanner({ isSpoofed, hiddenFromCustomer, chatExists: chatId !== undefined })
  const showPrivateChatBanner = spoofBanner === 'private' && !isPrivateChatBannerDismissed
  const showCustomerVisibleChatBanner = spoofBanner === 'customerVisible' && !isCustomerVisibleChatBannerDismissed
  const isLandingExperience = isLandingRoute && !hasMessages
  const isNewChatExperience = isNewChatRoute && !hasMessages
  const shouldShowNewChatComposer = isNewChatExperience
  const dismissStaffSpoofBanner = useCallback(() => {
    setIsStaffSpoofBannerDismissed(true)
  }, [])
  const dismissPrivateChatBanner = useCallback(() => {
    setIsPrivateChatBannerDismissed(true)
  }, [])
  const dismissCustomerVisibleChatBanner = useCallback(() => {
    setIsCustomerVisibleChatBannerDismissed(true)
  }, [])
  let content: React.ReactNode

  // Landing page or new chat — identical UI
  if (isLandingExperience || shouldShowNewChatComposer) {
    content = (
      <MainLayout>
        {showPrivateChatBanner && <PrivateChatBanner onDismiss={dismissPrivateChatBanner} />}
        {tripPlannerHeader}
        {showCustomerVisibleChatBanner && (
          <CustomerVisibleChatBanner onCreateNewChat={handleStartNewChat} onDismiss={dismissCustomerVisibleChatBanner} />
        )}
        <LandingPageWrapper>
          <ChatLandingPage
            onSubmit={handleSubmitWithTracking}
            onSubmitDirect={submitDirectWithTracking}
            onStaffChatOpen={canViewStaffChat ? handleStaffChatOpen : undefined}
            onAddTags={handleAddTags}
            disabled={isLoading}
            selectedTags={landingPageTags}
            onRemoveTag={handleLandingPageTagRemove}
            onBookingSelectionChange={setBookingSelection}
            onBookingItemsAdded={handleBookingItemsAdded}
          />
        </LandingPageWrapper>
      </MainLayout>
    )
  } else {
    content = (
      <MainLayout>
        {showStaffSpoofBanner && chatId !== undefined && <StaffSpoofBanner chatId={chatId} onDismiss={dismissStaffSpoofBanner} />}
        {showPrivateChatBanner && <PrivateChatBanner onDismiss={dismissPrivateChatBanner} />}
        {tripPlannerHeader}
        {showCustomerVisibleChatBanner && (
          <CustomerVisibleChatBanner onCreateNewChat={handleStartNewChat} onDismiss={dismissCustomerVisibleChatBanner} />
        )}
        <MapInteractionProvider value={mapInteractionValue}>
          <TripDrawerPresentationProvider>
            <ContentWrapper>
              <MainContent
                ref={mainContentRef}
                style={{ [LEFT_PANE_WIDTH_VAR]: `${leftPaneWidth.current}px` }}
              >
                <ChatColumnLayout>
                  <ChatContainer
                    chatId={chatId}
                    isLoggedIn={isLoggedIn}
                    isDevMode={isDevMode}
                    isSpoofed={isSpoofed}
                    hiddenFromCustomer={hiddenFromCustomer}
                    spoofingAgentEmail={spoofingAgentEmail}
                    navigateToNewChatRoute={navigateToNewChatRoute}
                    handleSubmit={handleSubmitWithTracking}
                    handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
                    activeMultiChoiceSelection={activeMultiChoiceSelection}
                    mobileSelectionText={mobileSelectionText}
                    offerContextTag={offerContextTag}
                    handleRemoveTag={handleRemoveTag}
                    selectedBookingItemIds={selectedBookingItemIds}
                    onBookingItemsAdded={handleBookingItemsAdded}
                    chatInputRef={chatInputRef}
                    showMessageTimeline
                  />
                </ChatColumnLayout>
                <PaneResizer $overlapSide="after" onMouseDown={handlePaneDragStart} />
                <RightPanelContainer />
                <DrawerContainer />
              </MainContent>
            </ContentWrapper>
          </TripDrawerPresentationProvider>
        </MapInteractionProvider>
      </MainLayout>
    )
  }

  return (
    <TripListPricingProvider>
      {content}
    </TripListPricingProvider>
  )
}

export default ChatPageContent
